import { TextInput } from '@ally/forms'
import { Accordion, Box, Link, TextBody, upToMd, get } from '@ally/metronome-ui'
import dayjs from 'dayjs'
import React from 'react'
import styled from 'styled-components'
import { BeneficiarySsnTaxId } from './BeneficiarySsnTaxId'

const StyledLink = styled(Link)`
  font-size: ${get.fontSizes('md')};
`
interface BeneIdentificationFieldProps {
  beneficiaryClassification: string
  changeBeneIdentificationFieldValue: (
    field: string,
    value: string | undefined,
  ) => void
  onBeneIdentificationChange: (e: React.BaseSyntheticEvent) => void
  ssnEditDisabled?: boolean
  issueDate: string
  expirationDate: string
  maskedSsn: string
  maskedTaxId: string
  identificationNumber: string
  beneIdentification: string
  setStepTwoFormErrorCode?: React.Dispatch<React.SetStateAction<string>>
  isIraPage?: boolean
}
const StyledDateBox = styled(Box)`
  span {
    white-space: nowrap;
  }
`

export const BeneficiaryIdentificationField: React.FC<
  BeneIdentificationFieldProps
> = ({
  issueDate,
  expirationDate,
  maskedSsn,
  maskedTaxId,
  identificationNumber,
  beneIdentification,
  beneficiaryClassification,
  changeBeneIdentificationFieldValue,
  onBeneIdentificationChange,
  ssnEditDisabled,
  setStepTwoFormErrorCode,
  isIraPage,
}) => {
  // istanbul ignore next - low value
  const onIssueDateChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    // we don't want to allow any digit other than numbers
    const regex = /[^0-9 //]/g
    if (regex.test(value)) {
      changeBeneIdentificationFieldValue(
        'issueDate',
        value.replace(/[^0-9 //]/g, ''),
      )
    } else if (value.length === 2 || value.length === 5) {
      const newText = `${value}/`
      changeBeneIdentificationFieldValue('issueDate', newText)
    } else if (value.length <= 10) {
      changeBeneIdentificationFieldValue('issueDate', value)
    }
  }

  // adding this logic to avoid user getting stuck when trying to backspace with keyboard
  // istanbul ignore next
  const checkIssueBackspace = (e: React.KeyboardEvent): void => {
    if (
      (e.key === 'Backspace' && issueDate[issueDate.length - 1] === '/') ||
      e.key === '/'
    ) {
      const newText = issueDate.slice(0, -1)
      changeBeneIdentificationFieldValue('issueDate', newText)
    }
  }

  // istanbul ignore next - low value
  const onExpirationDateChange = ({
    target: { value },
  }: React.BaseSyntheticEvent): void => {
    // we don't want to allow any digit other than numbers
    const regex = /[^0-9 //]/g
    if (regex.test(value)) {
      changeBeneIdentificationFieldValue(
        'expirationDate',
        value.replace(/[^0-9 //]/g, ''),
      )
    } else if (value.length === 2 || value.length === 5) {
      const newText = `${value}/`
      changeBeneIdentificationFieldValue('expirationDate', newText)
    } else if (value.length <= 10) {
      changeBeneIdentificationFieldValue('expirationDate', value)
    }
  }

  // adding this logic to avoid user getting stuck when trying to backspace with keyboard
  // istanbul ignore next
  const checkExpirationBackspace = (e: React.KeyboardEvent): void => {
    if (
      (e.key === 'Backspace' &&
        expirationDate[expirationDate.length - 1] === '/') ||
      e.key === '/'
    ) {
      const newText = expirationDate.slice(0, -1)
      changeBeneIdentificationFieldValue('expirationDate', newText)
    }
  }

  const validateIdentificationNumber = (val: string): string => {
    if (!val) {
      return 'Enter an identification number to continue.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateIssuer = (val: string): string => {
    if (!val) {
      return 'Enter an issuer to continue.'
    }
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const isValidDate = (date: string): boolean => {
    // istanbul ignore next // we already have a test for this
    return (
      dayjs(date, 'MM/DD/YYYY').format('MM/DD/YYYY') === date &&
      dayjs(date, 'MM/DD/YYYY', true).isValid()
    )
  }

  const validateIssueDate = (val: string): string => {
    if (!val) return 'Enter an issue date to continue.'
    // istanbul ignore next // we already have a test for this
    if (
      !isValidDate(val) ||
      dayjs(val).isAfter(dayjs()) ||
      dayjs(val).isBefore('12/31/1840')
    )
      return 'Enter a valid issue date.'
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  const validateExpirationDate = (val: string): string => {
    if (!val) return 'Enter an expiration date to continue.'
    // istanbul ignore next // we already have a test for this
    if (!isValidDate(val) || dayjs(val).isBefore(dayjs()))
      return 'Enter a valid expiration date.'
    // istanbul ignore next // we can remove this ignore once we apply an integration testing for this form
    return ''
  }

  return (
    <Box mb="md">
      <BeneficiarySsnTaxId
        beneficiaryClassification={beneficiaryClassification}
        changeBeneIdentificationFieldValue={changeBeneIdentificationFieldValue}
        onBeneIdentificationChange={onBeneIdentificationChange}
        maskedSsn={maskedSsn}
        maskedTaxId={maskedTaxId}
        identificationNumber={identificationNumber}
        beneIdentification={beneIdentification}
        ssnEditDisabled={ssnEditDisabled}
        setStepTwoFormErrorCode={setStepTwoFormErrorCode}
        isIraPage={isIraPage}
      />
      {beneIdentification &&
        beneIdentification !== 'SocialSecurityNumber' &&
        beneIdentification !== 'TaxId' && (
          <>
            <Box width={upToMd('88%', '50%')} mt="md">
              <TextInput
                isPrivate
                labelContent="Identification Number"
                name="identificationNumber"
                inputType="text"
                validate={validateIdentificationNumber}
                maxLength={20}
                onChange={(event): void =>
                  changeBeneIdentificationFieldValue(
                    'identificationNumber',
                    event.target.value,
                  )
                }
              />
            </Box>
            <Box width={upToMd('88%', '50%')} mt="md">
              <TextInput
                isPrivate
                labelContent="Issuer"
                name="issuer"
                inputType="text"
                validate={validateIssuer}
                maxLength={25}
                onChange={(event): void =>
                  changeBeneIdentificationFieldValue(
                    'issuer',
                    event.target.value,
                  )
                }
              />
            </Box>
            <StyledDateBox width={upToMd('88%', '50%')} mt="md">
              <TextInput
                isPrivate
                labelContent="Issue Date"
                name="issueDate"
                inputType="text"
                inputMode="numeric"
                validate={validateIssueDate}
                placeholder="   /    /"
                contextualHelp={{
                  variant: 'inline',
                  content: 'mm/dd/yyyy',
                }}
                onChange={onIssueDateChange}
                onKeyDown={checkIssueBackspace}
              />
            </StyledDateBox>
            <StyledDateBox width={upToMd('88%', '50%')} mt="md">
              <TextInput
                isPrivate
                labelContent="Expiration Date"
                name="expirationDate"
                inputType="text"
                inputMode="numeric"
                validate={validateExpirationDate}
                placeholder="   /    /"
                contextualHelp={{
                  variant: 'inline',
                  content: 'mm/dd/yyyy',
                }}
                onChange={onExpirationDateChange}
                onKeyDown={checkExpirationBackspace}
              />
            </StyledDateBox>
          </>
        )}
      <Accordion
        disableContainer
        items={[
          {
            heading: 'Why do we need this?',
            panel: (
              <>
                {isIraPage ? (
                  <TextBody size="md" tag="span" tabIndex={-1}>
                    We have to provide the beneficiary&apos;s Social Security
                    Number to the Internal Revenue Service upon your death.
                    It&apos;s safe to enter it here, but if you don&apos;t want
                    to enter this information online, you can print and complete
                    this{' '}
                    <StyledLink
                      href="https://staging.ally.com/content/dam/pdf/bank/change-beneficiary.pdf"
                      text="beneficiary form"
                      target="_blank"
                    />
                    .
                  </TextBody>
                ) : (
                  <TextBody size="md" tag="span" tabIndex={-1}>
                    The FDIC has made updates, effective April 1, 2020, to its
                    regulatory requirements for recordkeeping, requiring us to
                    maintain complete and accurate beneficiary information. To
                    meet the requirements, we need your help to provide us with
                    a valid government-issued ID number for each of your
                    beneficiaries.
                  </TextBody>
                )}
              </>
            ),
          },
        ]}
      />
    </Box>
  )
}
